/**
 * Shared activity-feed type metadata. Single source of truth so
 * quick-action buttons, entry badges, icons and labels stay in sync.
 *
 * `icon` is a lucide-react component (or null for entries that should
 * render without a timeline marker, like a plain Note).
 */

import {
	Send,
	Mail,
	PhoneOutgoing,
	PhoneMissed,
	Voicemail,
	PencilLine,
	BadgeCheck,
	CheckCircle2,
	CircleAlert,
	XCircle,
} from "lucide-react"

export const ACTIVITY_TYPES = {
	EMAIL_SENT: { label: "Email sent", badge: "INFO", icon: Send },
	EMAIL_RECEIVED: { label: "Email received", badge: "INFO", icon: Mail },
	CALL_MADE: { label: "Call made", badge: "POSITIVE", icon: PhoneOutgoing },
	// CALL_MISSED is no longer offered as a quick-action but stays here so
	// any historical entries continue to render with their proper label
	// and icon rather than falling back to the raw enum string.
	CALL_MISSED: { label: "Call missed", badge: "NEGATIVE", icon: PhoneMissed },
	VOICEMAIL_LEFT: { label: "Voicemail left", badge: "POSITIVE", icon: Voicemail },
	NOTE: { label: "Note", badge: "", icon: null },
	AMENDMENT_REQUESTED: { label: "Amendment requested", badge: "AMBER", icon: PencilLine },
	AMENDMENT_CONFIRMED: { label: "Amendment confirmed", badge: "POSITIVE", icon: BadgeCheck },
	AMENDMENT_OPENED: { label: "Amendment opened", badge: "AMBER", icon: PencilLine },
	AMENDMENT_FLAG_CHANGED: { label: "Flag changed", badge: "SPECIAL", icon: null },
	FOLLOW_UP_SCHEDULED: { label: "Follow up scheduled", badge: "AMBER", icon: null },
	STATUS_CHANGED: { label: "Status changed", badge: "SPECIAL", icon: null },
	AGENT_REASSIGNED: { label: "Agent reassigned", badge: "SPECIAL", icon: null },
	QUOTE_CHANGED: { label: "Quote changed", badge: "SPECIAL", icon: null },
	QUOTE_SENT: { label: "Quote sent", badge: "POSITIVE", icon: Send },
	TAG_ADDED: { label: "Tag added", badge: "", icon: null },
	TAG_REMOVED: { label: "Tag removed", badge: "", icon: null },
	CONFIRMATION_ADDED: { label: "Supplier confirmation added", badge: "POSITIVE", icon: CheckCircle2 },
	CONFIRMATION_UPDATED: { label: "Supplier confirmation updated", badge: "AMBER", icon: CircleAlert },
	CONFIRMATION_DELETED: { label: "Supplier confirmation deleted", badge: "NEGATIVE", icon: XCircle },
}

// Default quick-action set for the enquiry surface — comms + plain note.
// At enquiry stage there's nothing to amend yet, so booking-specific
// types are intentionally left out here.
export const QUICK_ACTION_TYPES = [
	"EMAIL_SENT",
	"EMAIL_RECEIVED",
	"CALL_MADE",
	"VOICEMAIL_LEFT",
	"NOTE",
]

// Quick-action set for the booking surface — same comms as above plus
// the amendment types that don't make sense pre-conversion.
export const BOOKING_QUICK_ACTION_TYPES = [
	"EMAIL_SENT",
	"EMAIL_RECEIVED",
	"CALL_MADE",
	"VOICEMAIL_LEFT",
	"AMENDMENT_REQUESTED",
	"AMENDMENT_CONFIRMED",
	"NOTE",
]

// Types that represent direct communication between an agent and the client.
// These render in the Activity feed (regardless of which surface they were
// logged from). CALL_MISSED stays in the list so any pre-existing missed-
// call entries still surface even though it's no longer offered as a
// quick-action.
export const COMMUNICATION_TYPES = [
	"EMAIL_SENT",
	"EMAIL_RECEIVED",
	"CALL_MADE",
	"CALL_MISSED",
	"VOICEMAIL_LEFT",
	"NOTE",
	"QUOTE_SENT",
	"AMENDMENT_REQUESTED",
	"AMENDMENT_CONFIRMED",
	"CONFIRMATION_ADDED",
	"CONFIRMATION_UPDATED",
	"CONFIRMATION_DELETED",
]

// Types that represent internal/system changes to the enquiry record.
// These render in the Change log card (separate from Activity).
export const SYSTEM_LOG_TYPES = [
	"STATUS_CHANGED",
	"AGENT_REASSIGNED",
	"FOLLOW_UP_SCHEDULED",
	"QUOTE_CHANGED",
	"TAG_ADDED",
	"TAG_REMOVED",
]

// Default placeholder body shown in the composer for each type
export const COMPOSER_PLACEHOLDERS = {
	EMAIL_SENT: "What did you send? e.g. \"Sent itinerary v2\"",
	EMAIL_RECEIVED: "What did the client say?",
	CALL_MADE: "What was discussed on the call?",
	VOICEMAIL_LEFT: "What did you say in the voicemail?",
	NOTE: "Add a note…",
	AMENDMENT_REQUESTED: "What change has the client asked for?",
	AMENDMENT_CONFIRMED: "What was changed and confirmed back to the client?",
}
